<script setup lang="ts">
import { useAsyncState } from '@vueuse/core'
import { dateUtils } from '@/helpers'
import { useI18n } from 'vue-i18n'

const props = defineProps({
  calendarId: {
    type: Number,
    default: -1,
    required: true
  },
  withChooseButton: {
    type: Boolean,
    default: true,
    required: false
  },

  duration: {
    type: Number,
    default: 60
  },
  isRangedBySlots: {
    type: Boolean,
    default: false
  },
  alterColorScheme: {
    type: Boolean,
    default: false,
    required: false
  }
})

const emit = defineEmits(['choose'])

const pages = ref(0)
const axios = useAxios()
const { formatToWeekdayAndDate, formatDateTime } = dateUtils()
const { locale } = useI18n()
const selectedDay = ref({ monthDayNumber: new Date().getDate(), date: new Date(), slots: null })
const attributes = ref([])
const selectedAppointmentTime = ref({})

const { isLoading, state: calendar, execute } = useAsyncState(
  async () => {
    const { data, headers } = await axios.get(`calendar/${props.calendarId}`, {
      params: {
        pageSizeInDays: 100,
        maxTimeSlotsPerDay: 30,
        duration: props.duration,
        isRangedBySlots: props.isRangedBySlots
      }
    })
    if (headers['x-total-pages']) {
      pages.value = parseInt(headers['x-total-pages'])
    }

    return data
  },
  [],
  {
    onSuccess: () => {
      setCalendarAvailableTimes()
    },
    onError: (error) => console.log(error)
  },
)

watch(() => props.duration, async () => {
  await execute()
  if (selectedDay.value?.date) updateSelectedDaySlots()
})

function updateSelectedDaySlots() {
  if (!calendar.value || !calendar.value.days) return

  const updatedDay = Object.values(calendar.value.days).find(day =>
    new Date(day.date).toDateString() === selectedDay.value.date.toDateString()
  )

  if (updatedDay) {
    selectedDay.value.slots = updatedDay.slots?.filter(slot => !slot.not_show) || []
    handleSlotClick(updatedDay.slots[0])
  }
}

function setCalendarAvailableTimes() {
  attributes.value = []
  const daysArray = Object.values(calendar.value?.days || {})

  daysArray.forEach((day) => {
    const visibleSlots = day.slots?.filter(slot => !slot.not_show) || []

    if (visibleSlots.length > 0) {
      const attribute = {
        customData: {
          count: visibleSlots.length,
          slots: visibleSlots
        },
        dates: new Date(day.date)
      }
      attributes.value.push(attribute)
    }
  })
}

function handleDayClick (day: any, attributes: any) {
  selectedDay.value.monthDayNumber = day.day
  selectedDay.value.date = day.date
  selectedDay.value.slots = attributes[0]?.customData?.slots
  
  // Automatically select first value
  if (selectedDay.value.slots) {
    
    handleSlotClick(selectedDay.value.slots[0])
  }
}

function handleSlotClick (slot: any) {
  selectedAppointmentTime.value = slot

  if(!props.withChooseButton)
    emit('choose', selectedAppointmentTime.value, selectedDay.value.date)
}

</script>
<template>
  <div class="w-full rounded-[20px] pb-6 md:pb-0"

  :class="props.alterColorScheme
        ? 'md:flex mt-10 md:mt-0 w-full bg-transparent'
        : 'w-full rounded-[20px] pb-6 md:pb-0'"

        >
    <div v-if="isLoading" class="flex justify-center h-[300px] items-center">
      <Spinner class="size-10" />
    </div>
    <div v-else class="md:flex mt-10 md:mt-0"
      
      >
      <div class="max-w-[475px] md:ml-6 mt-6">
        <VCalendar :locale="locale" expanded :attributes="attributes" borderless>
          <template #day-content="{ day, attributes }">
            <div class="m-1 rounded-[10px]" :class="selectedDay.monthDayNumber != day.day? 'bg-[#F7F8FD]' : 'bg-darkBlue text-white'">
              <button @click="handleDayClick(day, attributes)">
                <div class="md:w-[42px] xl:w-[50px] 2xl:w-[61px] h-[38px] md:h-[42px] xl:h-[50px] 2xl:h-[61px] rounded-[10px]">

                  <div class="absolute ml-1 xl:ml-2 2xl:text-lg">
                    {{day.day}}
                  </div>

                  <div class="text-white absolute bottom-2 xl:bottom-2.5 right-2">
                    <div v-for="(attr, index) in attributes" :key="index" class="flex justify-end">
                      <div class="bg-lightBlue rounded-full w-[16px] xs:w-[20px] xl:w-[22px] 2xl:w-[24px] h-[16px] xs:h-[20px] xl:h-[22px] 2xl:h-[24px] text-xs xs:text-sm xl:text-md bottom-[10px] leading-none flex justify-center items-center">
                        {{ attr.customData.count }}
                      </div>
                    </div>
                  </div>

                </div>
              </button>
            </div>
          </template>
        </VCalendar>
      </div>
      <div class="md:mt-6 lg:mt-10 mr-6 md:ml-12 w-full">
        <div class="mx-6 md:mx-0">
          {{ formatToWeekdayAndDate(selectedDay.date, locale) }}
          <div v-if="selectedDay.slots">

            <div v-for="slot in selectedDay.slots" :key="slot.time" class="mt-3">

              <button class="min-w-[110px] w-full h-11 border border-darkBlue rounded-[10px]"
              :class="{
                'bg-lightBlue border-none text-white': selectedAppointmentTime === slot,
                'text-lightBlue': props.alterColorScheme && selectedAppointmentTime !== slot,
              
                }"
              @click="handleSlotClick(slot)">
                {{ formatDateTime(new Date(selectedDay.date.toISOString().split('T')[0] + 'T' + slot.time + ':00Z'), 'HH:mm', locale, 'Europe/Vilnius') }} {{ $t('hour_abbreviation') }}.

              </button>
            </div>

            <div v-if="props.withChooseButton" class="mt-8 flex justify-center">
              <button class="mb-6 bg-darkBlue hover:bg-lightBlue text-white w-full h-11 rounded-full transition-all duration-50 active:scale-95" @click="emit('choose', selectedAppointmentTime, selectedDay.date)">{{ $t('choose') }}</button>
            </div>

          </div>
          <div v-else class="mt-3 font-normal">
            {{ $t('no_times_on_this_day') }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<style scoped>
:deep(.vc-weekday) {
  color: #0E1535;
  font-size: large;
  margin-top: 8px;
  margin-bottom: 2px;
}

:deep(.vc-title) {
  text-transform: capitalize;
  color: #0E1535;
  font-weight: 600;
  font-size: 18px;
}

:deep(.vc-arrow) {
  color: #0E1535;
  position: relative;
}

@media (min-width: 1025px) {
  :deep(.vc-next) { 
    right: 275%;
  }
}

@media (max-width: 1024px) and (min-width: 651px) {
  :deep(.vc-next) {
    right: 150%;
  }
}

@media (min-width: 1025px) {
  :deep(.vc-prev) {
    left: 275%;
  }
}

@media (max-width: 1024px) and (min-width: 651px) {
  :deep(.vc-prev) {
    left: 150%;
  }
}

:deep(.vc-arrow:hover) {
  background-color: #0E1535;
  color: white;
  border-radius: 10px;
}
</style>